import {Assert} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import {useThree} from "@react-three/fiber";
import React, {useEffect} from "react";
import * as THREE from "three";
import {Invalidate3JS} from "./Invalidate3JS.js";

export function useAutoInvalidate(componentName: string) {
	const {invalidate} = useThree();
	useEffect(()=>{
		// Route through the sanctioned Invalidate3JS wrapper so the Stats panel can show *why* the
		// map is rendering. The source includes the owning component name so a churning component
		// (one that re-renders every frame) is immediately identifiable.
		Invalidate3JS(invalidate, componentName, "observer_mgl_r3 component re-rendered");
	});
}

export class ObserverMGLR3Options {
	name?: string|n;
	useAutoInvalidate?: boolean|0|1 = true;
}

export function observer_mgl_r3<T>(compFunc: React.FC<T>): React.FC<T>;
export function observer_mgl_r3<T>(opts: ObserverMGLR3Options, compFunc: React.FC<T>): React.FC<T>;
export function observer_mgl_r3<T>(...args) {
	let opts: ObserverMGLR3Options, compFunc: React.FC<T>;
	if (args.length == 1) [opts, compFunc] = [new ObserverMGLR3Options(), args[0]];
	else /*if (args.length == 2)*/ [opts, compFunc] = args;
	const autoInvalidate = opts.useAutoInvalidate ?? true;
	const compName = opts.name || compFunc.displayName || compFunc.name || "Anonymous";
	const wrappedFunc: React.FC<T> = autoInvalidate
		? (props)=>{ useAutoInvalidate(compName); return (compFunc as any)(props); }
		: compFunc;
	return observer_mgl({bailHandler_opts: {loadingUI: ()=>null} as any}, wrappedFunc);
}

export function GetUVBounds(uvs: THREE.BufferAttribute) {
	let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
	for (let i = 0; i < uvs.count; i++) {
		const u = uvs.getX(i);
		const v = uvs.getY(i);
		minU = Math.min(minU, u);
		maxU = Math.max(maxU, u);
		minV = Math.min(minV, v);
		maxV = Math.max(maxV, v);
	}
	// Calculate UV space dimensions (for tiled textures, this will be > 1)
	const uvWidth = maxU - minU;
	const uvHeight = maxV - minV;
	//const uvArea = uvWidth * uvHeight;
	return {
		minU,
		maxU,
		minV,
		maxV,
		uvWidth,
		uvHeight
	};
}
export function NormalizeUVsToFillUVSpace(uvs: THREE.BufferAttribute, bounds: ReturnType<typeof GetUVBounds>) {
	Assert(uvs.itemSize == 2, "UVs should be a BufferAttribute with itemSize of 2 (x, y coordinates)");
	if (bounds.uvWidth == 0 || bounds.uvHeight == 0) return; // avoid division by zero

	for (let i = 0; i < uvs.array.length; i += 2) {
		// Normalize UV coordinates from their original range to 0-1
		const normalizedU = (uvs.array[i] - bounds.minU) / bounds.uvWidth;
		const normalizedV = (uvs.array[i + 1] - bounds.minV) / bounds.uvHeight;
		// Set the normalized UV coordinates back to the attribute
		uvs.setXY(i / 2, normalizedU, normalizedV); // i / 2, because the setXY function wants the index of the uv-pair, not the individual number
	}
	uvs.needsUpdate = true; // mark the UVs as needing an update
}